import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, LogOut, Inbox, ShoppingBag, BookOpen, Radio, Pencil, Trash2, Plus, FolderOpen, Copy, CheckCircle2, ExternalLink } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("pp_admin_key") || "");
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [courses, setCourses] = useState([]);
  const [files, setFiles] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();
  const [liveSessions, setLiveSessions] = useState([]);
  const emptyLive = { id: null, title: "", course_slug: "", scheduled_at: "", duration: "60 min", platform: "Zoom", join_url: "", description: "" };
  const [liveForm, setLiveForm] = useState(emptyLive);

  const saveLive = async () => {
    if (!liveForm.title || !liveForm.scheduled_at || !liveForm.join_url) {
      toast.error("Title, date/time and the Zoom link are required.");
      return;
    }
    const headers = { "X-Admin-Key": key };
    const payload = { title: liveForm.title, course_slug: liveForm.course_slug, scheduled_at: liveForm.scheduled_at, duration: liveForm.duration, platform: liveForm.platform, join_url: liveForm.join_url, description: liveForm.description };
    try {
      if (liveForm.id) {
        await api.put(`/admin/live-sessions/${liveForm.id}`, payload, { headers });
      } else {
        await api.post("/admin/live-sessions", payload, { headers });
      }
      toast.success("Live session saved.");
      const { data } = await api.get("/admin/live-sessions", { headers });
      setLiveSessions(data);
      setLiveForm(emptyLive);
    } catch {
      toast.error("Could not save the session.");
    }
  };

  const editLive = (s) => setLiveForm({ ...s, scheduled_at: (s.scheduled_at || "").slice(0, 16) });

  const deleteLive = async (id) => {
    try {
      await api.delete(`/admin/live-sessions/${id}`, { headers: { "X-Admin-Key": key } });
      setLiveSessions((ss) => ss.filter((s) => s.id !== id));
      toast.success("Session deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/purchases/${id}/approve`, {}, { headers: { "X-Admin-Key": key } });
      toast.success("Purchase approved — course unlocked for the student.");
      setPurchases((ps) => ps.map((p) => (p.id === id ? { ...p, status: "paid" } : p)));
    } catch {
      toast.error("Approve failed.");
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/admin/purchases/${id}/revoke`, {}, { headers: { "X-Admin-Key": key } });
      toast.success("Enrollment revoked — access locked for the student.");
      setPurchases((ps) => ps.map((p) => (p.id === id ? { ...p, status: "revoked" } : p)));
    } catch {
      toast.error("Revoke failed.");
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm("Delete this file? Any course notes linked to it will stop downloading.")) return;
    try {
      await api.delete(`/admin/files/${id}`, { headers: { "X-Admin-Key": key } });
      setFiles((fs) => fs.filter((f) => f.id !== id));
      toast.success("File deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const copyFileLink = async (f) => {
    const url = `${window.location.origin}/api/files/${f.storage_path}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(f.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Copy failed.");
    }
  };

  const load = async (k) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": k };
      const [e, p, c, l, f] = await Promise.all([
        api.get("/admin/enquiries", { headers }),
        api.get("/admin/purchases", { headers }),
        api.get("/admin/courses", { headers }),
        api.get("/admin/live-sessions", { headers }),
        api.get("/admin/files", { headers }),
      ]);
      setEnquiries(e.data);
      setPurchases(p.data);
      setCourses(c.data);
      setLiveSessions(l.data);
      setFiles(f.data);
      setAuthed(true);
      setKey(k);
      sessionStorage.setItem("pp_admin_key", k);
    } catch {
      toast.error("Invalid admin key.");
      sessionStorage.removeItem("pp_admin_key");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("pp_admin_key");
    setAuthed(false);
    setKey("");
    setInput("");
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login">
        <form
          onSubmit={(e) => { e.preventDefault(); load(input); }}
          className="w-full max-w-sm border border-white/10 bg-[#111111] p-10"
        >
          <div className="font-display text-2xl font-black tracking-tighter">PP<span className="text-brand">.</span>ADMIN</div>
          <p className="mt-2 text-sm text-zinc-500">Enter your admin key to view enquiries and purchases.</p>
          <input
            data-testid="admin-key-input"
            type="password"
            required
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin key"
            className="mt-8 w-full rounded-none border-b border-[#27272A] bg-transparent py-4 text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-2 bg-brand px-6 py-3.5 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:scale-95 disabled:opacity-50"
          >
            {loading && <Loader2 size={14} className="animate-spin" />} Unlock Dashboard
          </button>
        </form>
      </div>
    );
  }

  const rows = tab === "enquiries" ? enquiries : purchases;

  return (
    <div className="min-h-screen px-6 py-12 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="font-display text-3xl font-black tracking-tighter">PP<span className="text-brand">.</span>ADMIN</div>
            <p className="mt-1 text-sm text-zinc-500">Enquiries and purchases, live from ppacedemy.in</p>
          </div>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-white">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4">
          <div className="bg-[#0a0a0a] p-6" data-testid="admin-stat-enquiries">
            <div className="font-display text-3xl font-black">{enquiries.length}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500">Enquiries</div>
          </div>
          <div className="bg-[#0a0a0a] p-6" data-testid="admin-stat-purchases">
            <div className="font-display text-3xl font-black">{purchases.filter((p) => p.status === "paid").length}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500">Paid Orders</div>
          </div>
          <div className="bg-[#0a0a0a] p-6" data-testid="admin-stat-revenue">
            <div className="font-display text-3xl font-black">₹{purchases.filter((p) => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0).toLocaleString("en-IN")}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500">Revenue</div>
          </div>
          <div className="bg-[#0a0a0a] p-6" data-testid="admin-stat-pending">
            <div className="font-display text-3xl font-black">{purchases.filter((p) => p.status !== "paid").length}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500">Abandoned</div>
          </div>
        </div>

        <div className="mt-10 flex gap-2" data-testid="admin-tabs">
          <button data-testid="admin-tab-enquiries" onClick={() => setTab("enquiries")} className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${tab === "enquiries" ? "border-brand bg-brand text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <Inbox size={14} /> Enquiries
          </button>
          <button data-testid="admin-tab-purchases" onClick={() => setTab("purchases")} className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${tab === "purchases" ? "border-brand bg-brand text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <ShoppingBag size={14} /> Purchases
          </button>
          <button data-testid="admin-tab-courses" onClick={() => setTab("courses")} className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${tab === "courses" ? "border-brand bg-brand text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <BookOpen size={14} /> Courses
          </button>
          <button data-testid="admin-tab-live" onClick={() => setTab("live")} className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${tab === "live" ? "border-brand bg-brand text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <Radio size={14} /> Live
          </button>
          <button data-testid="admin-tab-files" onClick={() => setTab("files")} className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${tab === "files" ? "border-brand bg-brand text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <FolderOpen size={14} /> Files
          </button>
        </div>

        {tab === "files" ? (
          <div className="mt-6" data-testid="admin-files">
            <p className="mb-6 text-sm text-zinc-500">Every PDF and image you've uploaded. Copy a link to paste into course notes, or delete files you no longer need.</p>
            {files.length === 0 ? (
              <p className="border border-white/10 p-10 text-sm text-zinc-500" data-testid="files-empty-state">No files yet — upload PDFs from any course's editor (PDF Notes section) and they'll appear here.</p>
            ) : (
              <div className="divide-y divide-white/10 border border-white/10">
                {files.map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 transition-colors duration-200 hover:bg-white/5" data-testid={`file-row-${f.id}`}>
                    <div className="min-w-0">
                      <div className="truncate font-display text-sm font-bold tracking-tight md:text-base">{f.original_filename}</div>
                      <div className="mt-1 text-xs uppercase tracking-widest text-zinc-500">
                        {f.content_type} · {(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <a href={`/api/files/${f.storage_path}`} target="_blank" rel="noopener noreferrer" data-testid={`file-open-${f.id}`} className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-200 hover:border-brand hover:text-white">
                        <ExternalLink size={13} /> Open
                      </a>
                      <button onClick={() => copyFileLink(f)} data-testid={`file-copy-${f.id}`} className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-200 hover:border-brand hover:text-white">
                        {copiedId === f.id ? <CheckCircle2 size={13} className="text-emerald-500" /> : <Copy size={13} />} {copiedId === f.id ? "Copied" : "Copy link"}
                      </button>
                      <button onClick={() => deleteFile(f.id)} data-testid={`file-delete-${f.id}`} className="flex items-center gap-1.5 border border-red-500/40 px-3 py-2 text-xs font-bold uppercase tracking-widest text-red-400 transition-colors duration-200 hover:bg-red-500 hover:text-white">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "live" ? (
          <div className="mt-6 border border-white/10 p-8" data-testid="admin-live-editor">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Session title</label>
                <input data-testid="live-title-input" value={liveForm.title} onChange={(e) => setLiveForm({ ...liveForm, title: e.target.value })} placeholder="Market Open Live — Nifty Analysis" className="mt-2 w-full border-b border-[#27272A] bg-transparent py-3 text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Linked course</label>
                <select data-testid="live-course-select" value={liveForm.course_slug} onChange={(e) => setLiveForm({ ...liveForm, course_slug: e.target.value })} className="mt-2 w-full cursor-pointer border-b border-[#27272A] bg-[#0a0a0a] py-3 text-white outline-none">
                  <option value="">General (all students)</option>
                  {courses.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Date & time</label>
                <input data-testid="live-datetime-input" type="datetime-local" value={liveForm.scheduled_at} onChange={(e) => setLiveForm({ ...liveForm, scheduled_at: e.target.value })} className="mt-2 w-full border-b border-[#27272A] bg-transparent py-3 text-white outline-none transition-colors duration-300 focus:border-white [color-scheme:dark]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">Duration</label>
                <input data-testid="live-duration-input" value={liveForm.duration} onChange={(e) => setLiveForm({ ...liveForm, duration: e.target.value })} placeholder="60 min" className="mt-2 w-full border-b border-[#27272A] bg-transparent py-3 text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">Zoom join link (hidden from non-paying visitors)</label>
                <input data-testid="live-join-url-input" value={liveForm.join_url} onChange={(e) => setLiveForm({ ...liveForm, join_url: e.target.value })} placeholder="https://zoom.us/j/..." className="mt-2 w-full border-b border-[#27272A] bg-transparent py-3 text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">Description (optional)</label>
                <textarea data-testid="live-description-input" rows={2} value={liveForm.description} onChange={(e) => setLiveForm({ ...liveForm, description: e.target.value })} placeholder="What you'll cover in this session..." className="mt-2 w-full resize-y border-b border-[#27272A] bg-transparent py-3 text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white" />
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="live-save-button" onClick={saveLive} className="bg-brand px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95">
                {liveForm.id ? "Update Session" : "Add Session"}
              </button>
              {liveForm.id && (
                <button data-testid="live-cancel-edit-button" onClick={() => setLiveForm(emptyLive)} className="border border-white/15 px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-300 hover:border-white/40">
                  Cancel Edit
                </button>
              )}
            </div>
            <div className="mt-10 border-t border-white/10 pt-6" data-testid="live-sessions-list">
              {liveSessions.length === 0 ? (
                <p className="text-sm text-zinc-500">No sessions scheduled yet.</p>
              ) : (
                liveSessions.map((s) => (
                  <div key={s.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-white/5 py-4" data-testid={`live-row-${s.id}`}>
                    <div>
                      <div className="text-sm font-medium text-white">{s.title}</div>
                      <div className="mt-1 text-xs text-zinc-500">{fmt(s.scheduled_at)} · {s.duration} · {s.platform}</div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`live-edit-${s.id}`} onClick={() => editLive(s)} className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-300 transition-colors duration-300 hover:border-brand hover:text-white"><Pencil size={12} /> Edit</button>
                      <button data-testid={`live-delete-${s.id}`} onClick={() => deleteLive(s.id)} className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-500 transition-colors duration-300 hover:border-red-500 hover:text-red-400"><Trash2 size={12} /> Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : tab === "courses" ? (
          <div className="mt-6" data-testid="admin-course-editor">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-zinc-500">Full control — edit details, lessons, explanations, quizzes, notes and question banks.</p>
              <button data-testid="new-course-button" onClick={() => navigate("/admin/editor/new")} className="flex shrink-0 items-center gap-2 bg-brand px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95">
                <Plus size={14} /> New Course
              </button>
            </div>
            <div className="divide-y divide-white/10 border border-white/10">
              {courses.map((c) => (
                <button key={c.slug} data-testid={`edit-course-${c.slug}`} onClick={() => navigate(`/admin/editor/${c.slug}`)} className="group flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-300 hover:bg-white/5">
                  <div>
                    <div className="font-display text-base font-bold tracking-tight md:text-lg">{c.title}</div>
                    <div className="mt-1 text-xs text-zinc-500">{(c.lessons || []).length} chapters · {(c.question_bank || []).length} bank questions · ₹{(c.price || 0).toLocaleString("en-IN")}</div>
                  </div>
                  <span className="flex shrink-0 items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-300 group-hover:text-brand">
                    <Pencil size={14} /> Edit
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
        <div className="mt-6 overflow-x-auto border border-white/10" data-testid="admin-table">
          {rows.length === 0 ? (
            <p className="p-10 text-sm text-zinc-500" data-testid="admin-empty-state">Nothing here yet — {tab === "enquiries" ? "enquiries from the contact form" : "course purchases"} will appear here.</p>
          ) : tab === "enquiries" ? (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-zinc-600">
                  <th className="px-6 py-4">When</th><th className="px-6 py-4">Name</th><th className="px-6 py-4">Contact</th><th className="px-6 py-4">Interest</th><th className="px-6 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-white/5 transition-colors duration-200 hover:bg-white/5" data-testid={`enquiry-row-${e.id}`}>
                    <td className="whitespace-nowrap px-6 py-4 text-zinc-500">{fmt(e.created_at)}</td>
                    <td className="px-6 py-4 font-medium">{e.name}</td>
                    <td className="px-6 py-4 text-zinc-400">{e.email}<br /><span className="text-zinc-600">{e.phone}</span></td>
                    <td className="px-6 py-4"><span className="bg-brand/10 px-2.5 py-1 text-xs text-brand">{e.interest}</span></td>
                    <td className="max-w-xs px-6 py-4 text-zinc-400">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-zinc-600">
                  <th className="px-6 py-4">When</th><th className="px-6 py-4">Student</th><th className="px-6 py-4">Course</th><th className="px-6 py-4">Amount</th><th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 transition-colors duration-200 hover:bg-white/5" data-testid={`purchase-row-${p.id}`}>
                    <td className="whitespace-nowrap px-6 py-4 text-zinc-500">{fmt(p.created_at)}</td>
                    <td className="px-6 py-4 font-medium">{p.name}<br /><span className="text-zinc-600">{p.email}</span></td>
                    <td className="px-6 py-4 text-zinc-400">
                      {p.course_title}
                      {p.method === "upi" && <span className="block text-xs text-zinc-600">UPI · UTR {p.payment_id}</span>}
                    </td>
                    <td className="px-6 py-4">₹{(p.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-bold uppercase tracking-widest ${p.status === "paid" ? "bg-emerald-500/10 text-emerald-500" : p.status === "upi_pending" ? "bg-amber-500/10 text-amber-500" : p.status === "revoked" ? "bg-red-500/10 text-red-400" : "bg-zinc-500/10 text-zinc-500"}`} data-testid={`purchase-status-${p.id}`}>
                        {p.status === "upi_pending" ? "UPI pending" : p.status}
                      </span>
                      {p.status === "upi_pending" && (
                        <button data-testid={`approve-purchase-${p.id}`} onClick={() => approve(p.id)} className="ml-3 border border-emerald-500/40 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-emerald-500 transition-colors duration-200 hover:bg-emerald-500 hover:text-white">
                          Approve
                        </button>
                      )}
                      {p.status === "paid" && p.method === "upi" && (
                        <button data-testid={`revoke-purchase-${p.id}`} onClick={() => revoke(p.id)} className="ml-3 border border-red-500/40 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-red-400 transition-colors duration-200 hover:bg-red-500 hover:text-white">
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
